import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Activity,
  AppWindow,
  Bell,
  Bot,
  Building2,
  GitBranch,
  Link2,
  ListTodo,
  Lock,
  Puzzle,
  Shield,
  User,
  Users,
  UsersRound,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CenterPanel } from "@/components/layout/center-panel";
import { AccountSettingsContent } from "@/pages/Account";
import { TenantAuthSettings } from "@/pages/tenant/settings/TenantAuthSettings";
import { TenantMcpDetail } from "@/pages/tenant/settings/TenantMcpDetail";
import { TenantMcpList } from "@/pages/tenant/settings/TenantMcpList";
import { TenantCompanyProfile } from "@/pages/tenant/settings/TenantCompanyProfile";
import { TenantSkillsSettings } from "@/pages/tenant/settings/TenantSkillsSettings";
import { TenantVaultSettings } from "@/pages/tenant/settings/TenantVaultSettings";
import { Apps } from "@/pages/tenant/Apps";
import { AppBuilder } from "@/pages/tenant/AppBuilder";
import { AppDetail } from "@/pages/tenant/AppDetail";
import { Agents } from "@/pages/tenant/Agents";
import { NewAgent } from "@/pages/tenant/NewAgent";
import { NewSubagent } from "@/pages/tenant/NewSubagent";
import { AgentDetail } from "@/pages/tenant/AgentDetail";
import { DebugSessions } from "@/pages/tenant/DebugSessions";
import { GroupManagement } from "@/pages/tenant/GroupManagement";
import { M365Settings } from "@/pages/tenant/M365Settings";
import { Notifications } from "@/pages/tenant/Notifications";
import { OrgChart } from "@/pages/tenant/OrgChart";
import { Tasks } from "@/pages/tenant/Tasks";
import { UserManagement } from "@/pages/tenant/UserManagement";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { cn } from "@/lib/utils";

// One menu, no menu inside it (2026-09-29). What used to be "Tenant settings"
// with its own inner menu is now spread across Agent tools and Admin.
type DialogSection =
  // Account and Workspace: everyone
  | "account"
  | "apps"
  | "agents"
  | "tasks"
  // Issues is hidden from the popup for now (2026-10-02): adding one never
  // reaches the agent, so it read as a work queue that does nothing. Waiting
  // on Michael, with Goals. The pages and the /issues address still exist;
  // bringing it back is this section, its nav button and its content lines.
  | "org"
  // Agent tools: agent admins and tenant admins
  | "mcp"
  | "skills"
  // Admin: tenant admins only. Users and Groups are one "Members" page.
  // Usage Metrics is hidden from the popup for now (2026-09-29): workflow
  // Insights covers what people use it for. The old /settings/usage page
  // still exists.
  | "profile"
  | "members"
  | "auth"
  | "vault"
  | "notifications"
  | "m365"
  // Diagnostics: Aztech staff only
  | "debug";
/** The sections a `{ kind: "tenant" }` caller can ask for. "profile" was the
 *  old Overview page. */
export type TenantSettingsSection = "profile" | "auth" | "vault" | "mcp" | "skills";
type EmbeddedAppView =
  | { kind: "list" }
  | { kind: "detail"; appId: string }
  | { kind: "builder"; appId: string };

export type TenantSettingsDialogState =
  | { kind: "account" }
  /** `create` opens that section's create form rather than its list. */
  | { kind: "tenant"; section?: TenantSettingsSection; create?: boolean }
  /** `create` opens the New agent wizard (tenant admins only).
   *  `subagentParentId` opens the sub-agent wizard for that parent. */
  | { kind: "agent"; agentId?: string; create?: boolean; subagentParentId?: string }
  | null;

// Items hidden by role, matching what the server lets each role change.
const agentToolSections: DialogSection[] = ["mcp", "skills"];
const adminSections: DialogSection[] = [
  "profile",
  "members",
  "auth",
  "vault",
  "notifications",
  "m365",
];

export function TenantSettingsDialog({
  state,
  onClose,
}: {
  state: TenantSettingsDialogState;
  onClose: () => void;
}) {
  const {
    tenantId,
    isTenantAdmin,
    isAgentAdmin,
    isInstanceAdmin,
  } = useTenant();
  const [searchParams] = useSearchParams();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [activeSection, setActiveSection] = useState<DialogSection>("account");
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [subagentParentId, setSubagentParentId] = useState<string | null>(null);
  const [appView, setAppView] = useState<EmbeddedAppView>({ kind: "list" });
  // The MCP open in its details view, if any. Clicking "MCP Servers" in the
  // menu always returns to the list.
  const [openMcpId, setOpenMcpId] = useState<string | null>(null);
  // Set when the caller asked for a create form, cleared as soon as the user
  // moves section, so coming back gives the plain list.
  const [startInCreate, setStartInCreate] = useState(false);
  const canUseAgentTools = isTenantAdmin || isAgentAdmin;
  const canOpen = (section: DialogSection) =>
    adminSections.includes(section)
      ? isTenantAdmin
      : agentToolSections.includes(section)
        ? canUseAgentTools
        : section === "debug"
          ? isInstanceAdmin
          : true;
  const urlAgentId = searchParams.get("agentId");
  const defaultAgentId = useMemo(() => {
    if (urlAgentId && agents.some((agent) => agent.id === urlAgentId)) return urlAgentId;
    return agents[0]?.id ?? null;
  }, [agents, urlAgentId]);
  const selectedAgentId =
    activeAgentId && agents.some((agent) => agent.id === activeAgentId)
      ? activeAgentId
      : null;
  const setSection = (section: DialogSection) => {
    setActiveSection(section);
    setCreatingAgent(false);
    setSubagentParentId(null);
    setStartInCreate(false);
    if (section === "mcp") setOpenMcpId(null);
    if (section === "apps") setAppView({ kind: "list" });
  };

  useEffect(() => {
    if (!state) return;

    if (state.kind === "account") setSection("account");
    if (state.kind === "tenant") {
      // With no section asked for, land on the first one this role can open.
      const requested: DialogSection =
        state.section ?? (isTenantAdmin ? "profile" : "mcp");
      const allowed = canOpen(requested);
      setSection(allowed ? requested : "account");
      // Only honour the create form on the section that was actually asked for.
      setStartInCreate(allowed && state.create === true);
    }
    if (state.kind === "agent") {
      setSection("agents");
      // Only a tenant admin may create an agent (the server refuses anyone
      // else), so the wizard is only opened for them. Closing it lands on the
      // agent list rather than whichever agent happened to be selected.
      if (state.create && isTenantAdmin) {
        setActiveAgentId(null);
        setCreatingAgent(true);
      } else if (state.subagentParentId) {
        // Closing the sub-agent wizard returns to the parent agent.
        setActiveAgentId(state.subagentParentId);
        setSubagentParentId(state.subagentParentId);
      } else {
        setActiveAgentId(state.agentId ?? defaultAgentId);
      }
    }
    // canOpen only reads the role flags listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, defaultAgentId, isTenantAdmin, isAgentAdmin, isInstanceAdmin]);

  useEffect(() => {
    if (!state) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onClose]);

  if (!state) return null;

  // Sessions debug refuses everyone but Aztech staff, so only they see it.
  const showDiagnostics = isInstanceAdmin;
  const isAgentDetailOpen = activeSection === "agents" && !!selectedAgentId;
  const isAgentCreateOpen =
    activeSection === "agents" && (creatingAgent || !!subagentParentId);
  const isFullBleedView =
    isAgentDetailOpen || isAgentCreateOpen || activeSection === "org";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-5">
      <div
        className="absolute inset-0"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative flex h-[min(860px,calc(100vh-36px))] w-[min(1680px,calc(100vw-36px))] overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
        <aside className="flex w-[250px] shrink-0 flex-col border-r border-border bg-sidebar px-2 py-4">
          <nav className="min-h-0 flex-1 overflow-y-auto sidebar-scroll">
            <DialogNavGroup label="Account">
              <DialogNavButton
                active={activeSection === "account"}
                icon={User}
                label="My account"
                onClick={() => setSection("account")}
              />
            </DialogNavGroup>

            <DialogNavGroup label="Workspace">
              <DialogNavButton active={activeSection === "apps"} icon={AppWindow} label="Apps" onClick={() => setSection("apps")} />
              <DialogNavButton
                active={activeSection === "agents"}
                icon={Bot}
                label="Agents"
                dataTour="settings-nav-agents"
                onClick={() => {
                  setSection("agents");
                  setActiveAgentId(null);
                }}
              />
              <DialogNavButton active={activeSection === "tasks"} icon={ListTodo} label="Tasks" onClick={() => setSection("tasks")} />
              <DialogNavButton
                active={activeSection === "org"}
                icon={GitBranch}
                label="Organisation"
                onClick={() => setSection("org")}
              />
            </DialogNavGroup>

            {canUseAgentTools && (
              <DialogNavGroup label="Agent tools">
                <DialogNavButton
                  active={activeSection === "mcp"}
                  icon={Puzzle}
                  label="MCP Servers"
                  dataTour="tenant-nav-mcp"
                  onClick={() => setSection("mcp")}
                />
                <DialogNavButton active={activeSection === "skills"} icon={Zap} label="Skills" onClick={() => setSection("skills")} />
              </DialogNavGroup>
            )}

            {isTenantAdmin && (
              <DialogNavGroup label="Admin">
                <DialogNavButton active={activeSection === "profile"} icon={Building2} label="Company Profile" onClick={() => setSection("profile")} />
                <DialogNavButton active={activeSection === "members"} icon={Users} label="Members" onClick={() => setSection("members")} />
                <DialogNavButton active={activeSection === "notifications"} icon={Bell} label="Notifications" onClick={() => setSection("notifications")} />
                <DialogNavButton active={activeSection === "m365"} icon={Link2} label="Microsoft 365" onClick={() => setSection("m365")} />
                <DialogNavButton active={activeSection === "auth"} icon={Shield} label="Authentication" onClick={() => setSection("auth")} />
                <DialogNavButton active={activeSection === "vault"} icon={Lock} label="Key Vault" onClick={() => setSection("vault")} />
              </DialogNavGroup>
            )}

            {showDiagnostics && (
              <DialogNavGroup label="Diagnostics">
                <DialogNavButton active={activeSection === "debug"} icon={Activity} label="Sessions debug" onClick={() => setSection("debug")} />
              </DialogNavGroup>
            )}
          </nav>
        </aside>

        <section className="relative flex min-w-0 flex-1 flex-col">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4 z-10 h-8 w-8 p-0 text-text-muted hover:text-text-primary"
            onClick={onClose}
            aria-label="Close settings"
            title="Close"
          >
            <X size={16} />
          </Button>
          <div
            className={cn(
              "min-h-0 flex-1",
              isFullBleedView ? "overflow-hidden p-0" : "overflow-y-auto px-10 py-10",
            )}
          >
            <div className={cn("w-full", isFullBleedView ? "h-full" : "mx-auto max-w-[1200px]")}>
              {activeSection === "account" && (
                <CenterPanel title="My account">
                  <AccountSettingsContent showBack={false} showTitle={false} compactAccess />
                </CenterPanel>
              )}
              {activeSection === "org" && (
                <OrgChart
                  embedded
                  onOpenAgent={(agentId) => {
                    setSection("agents");
                    setActiveAgentId(agentId);
                  }}
                  onLeave={onClose}
                />
              )}
              {activeSection === "apps" && appView.kind === "list" && (
                <Apps
                  embedded
                  onOpenApp={(appId) => setAppView({ kind: "detail", appId })}
                  onOpenBuilder={(appId) => setAppView({ kind: "builder", appId })}
                />
              )}
              {activeSection === "apps" && appView.kind === "detail" && (
                <AppDetail
                  appId={appView.appId}
                  onBack={() => setAppView({ kind: "list" })}
                />
              )}
              {activeSection === "apps" && appView.kind === "builder" && (
                <AppBuilder
                  appId={appView.appId}
                  onBack={() => setAppView({ kind: "list" })}
                />
              )}
              {activeSection === "agents" && (
                creatingAgent ? (
                  <NewAgent
                    embedded
                    onClose={() => setCreatingAgent(false)}
                    onCreated={(agentId) => {
                      setCreatingAgent(false);
                      setActiveAgentId(agentId);
                    }}
                  />
                ) : subagentParentId ? (
                  <NewSubagent
                    embedded
                    parentAgentId={subagentParentId}
                    onClose={() => setSubagentParentId(null)}
                    onCreated={(agentId) => {
                      setSubagentParentId(null);
                      setActiveAgentId(agentId);
                    }}
                  />
                ) : selectedAgentId ? (
                  <div className="flex h-full flex-col overflow-hidden">
                    <div className="shrink-0 border-b border-border bg-surface px-4 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => setActiveAgentId(null)}
                      >
                        Back to agents
                      </Button>
                    </div>
                    <div className="min-h-0 flex-1">
                      {/* Keyed by agent so opening a sub-agent starts on its
                          Config tab, not the parent's Subagents tab. */}
                      <AgentDetail
                        key={selectedAgentId}
                        agentId={selectedAgentId}
                        embedded
                        onOpenAgent={(agentId) => setActiveAgentId(agentId)}
                        onCreateSubagent={(parentId) => setSubagentParentId(parentId)}
                      />
                    </div>
                  </div>
                ) : (
                  <Agents
                    embedded
                    onOpenAgent={(agentId) => setActiveAgentId(agentId)}
                    onRequestClose={onClose}
                    onCreateInstance={() => setCreatingAgent(true)}
                    onCreateSubagent={(parentId) => setSubagentParentId(parentId)}
                  />
                )
              )}
              {activeSection === "tasks" && <Tasks />}
              {activeSection === "mcp" && canUseAgentTools && (
                <CenterPanel title="MCP Servers">
                  {/* Opening an MCP stays inside the popup; the list used to
                      navigate to the full /settings/mcp/:id page behind it. */}
                  {openMcpId ? (
                    <TenantMcpDetail mcpId={openMcpId} onBack={() => setOpenMcpId(null)} />
                  ) : (
                    <TenantMcpList startInCreate={startInCreate} onOpenMcp={setOpenMcpId} />
                  )}
                </CenterPanel>
              )}
              {activeSection === "skills" && canUseAgentTools && (
                <CenterPanel title="Skills">
                  <TenantSkillsSettings />
                </CenterPanel>
              )}
              {isTenantAdmin && (
                <>
                  {activeSection === "profile" && (
                    <CenterPanel title="Company Profile">
                      <TenantCompanyProfile />
                    </CenterPanel>
                  )}
                  {activeSection === "members" && <MembersPanel />}
                  {activeSection === "auth" && (
                    <CenterPanel title="Authentication">
                      <TenantAuthSettings />
                    </CenterPanel>
                  )}
                  {activeSection === "vault" && (
                    <CenterPanel title="Key Vault">
                      <TenantVaultSettings />
                    </CenterPanel>
                  )}
                  {activeSection === "notifications" && <Notifications />}
                  {activeSection === "m365" && <M365Settings />}
                </>
              )}
              {activeSection === "debug" && showDiagnostics && <DebugSessions />}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function DialogNavGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
        {label}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// Users and Groups on one page. Both are about people and what they can reach
// (a group hands its members access to agents; each person's own role decides
// what they can do with them), so they were two menu items for one job.
function MembersPanel() {
  // One page, two sections: Users first, Groups underneath (Kenneth preferred
  // this to tabs). Each section keeps its own counts and buttons.
  return (
    <CenterPanel title="Members">
      <section aria-labelledby="members-users">
        <h2 id="members-users" className="mb-3 flex items-center gap-2 text-base font-semibold text-text-primary">
          <Users size={16} className="text-accent" />
          Users
        </h2>
        <UserManagement bare />
      </section>
      <section aria-labelledby="members-groups" className="mt-10 border-t border-border pt-8">
        <h2 id="members-groups" className="mb-3 flex items-center gap-2 text-base font-semibold text-text-primary">
          <UsersRound size={16} className="text-accent" />
          Groups
        </h2>
        <GroupManagement bare />
      </section>
    </CenterPanel>
  );
}

function DialogNavButton({
  active = false,
  icon: Icon,
  label,
  onClick,
  dataTour,
}: {
  active?: boolean;
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  dataTour?: string;
}) {
  return (
    <button
      type="button"
      data-tour={dataTour}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors",
        active
          ? "bg-accent/10 font-medium text-accent"
          : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
      )}
    >
      <Icon size={14} />
      <span className="truncate">{label}</span>
    </button>
  );
}
